/**
 * Every suggestion Claude made for the run: pending ones first, decided ones folded away.
 * A judge-flag suggestion can open its case, so the reviewer reads the output before
 * deciding; the same suggestion also shows on its trial there.
 */
import type { EvalSuggestion, StoredTrialResult } from '../../types/evalTypes';
import { SuggestionCard } from './SuggestionCard';

interface SuggestionsPanelProps {
  suggestions: EvalSuggestion[];
  results: StoredTrialResult[];
  currentUid: string | undefined;
  onShow: (caseId: string, armId: string) => void;
  onDecide: (suggestionId: string, accept: boolean, rejectReason?: string) => Promise<void>;
}

export const SuggestionsPanel = ({ suggestions, results, currentUid, onShow, onDecide }: SuggestionsPanelProps) => {
  if (suggestions.length === 0) return null;

  const pending = suggestions.filter((suggestion) => suggestion.status === 'pending');
  const decided = suggestions.filter((suggestion) => suggestion.status !== 'pending');

  const card = (suggestion: EvalSuggestion) => {
    const result = suggestion.kind === 'judge-flag' ? results.find((candidate) => candidate.resultId === suggestion.resultId) : undefined;
    return (
      <SuggestionCard
        key={suggestion.suggestionId}
        suggestion={suggestion}
        currentUid={currentUid}
        context={
          result && (
            <p className="text-gray-600">
              <span className="font-mono">{result.caseId}</span> · arm <span className="font-mono">{result.armId}</span> · trial{' '}
              {result.trial}{' '}
              <button type="button" onClick={() => onShow(result.caseId, result.armId)} className="text-blue-600 hover:underline">
                Show outputs
              </button>
            </p>
          )
        }
        onDecide={(accept, rejectReason) => onDecide(suggestion.suggestionId, accept, rejectReason)}
      />
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Suggestions from Claude</h2>
        <p className="text-sm text-gray-500">
          {pending.length > 0
            ? `${pending.length} pending. Nothing changes until you accept; then the flag or review is in your name.`
            : 'None pending.'}
        </p>
      </div>
      {pending.map(card)}
      {decided.length > 0 && (
        <details>
          <summary className="text-sm text-gray-600 cursor-pointer">{decided.length} decided</summary>
          <div className="space-y-2 mt-2">{decided.map(card)}</div>
        </details>
      )}
    </div>
  );
};
